import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { PublicPage } from 'api/website';
import { hasBackground } from 'components/Cms/getContentBlock';
import SearchToolbar from 'components/SearchToolbar';
import StaffTools from 'components/StaffTools';
import Layout from 'layouts/Layout';
import AmpContentPage from 'pages/AmpContentPage';
import ContentPage from 'pages/ContentPage';
import { updateCurrentUser } from 'store/main/ducks/currentUser';
import { pushFlashMessage } from 'store/main/ducks/flashMessages';
import AmpContext from 'utils/AmpContext';
import { isStaff as userIsStaff } from 'utils/currentUser';
import { isDefined } from 'utils/functional';
import { setDocumentTitle } from 'utils/meta';
import { scrollToHash } from 'utils/scrollIntoView';
import { getParam, hasParam } from 'utils/url';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const ContentPageContainer = ({
  content,
  defaultSearchType,
}: {
  content: PublicPage;
  defaultSearchType?: ListingType;
}) => {
  const {
    currentUser,
    gMapsError: { errored },
  } = useSelector((state) => ({
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'currentUser' does not exist on type 'Def... Remove this comment to see the full error message
    currentUser: state.currentUser,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'gMapsError' does not exist on type 'Defa... Remove this comment to see the full error message
    gMapsError: state.gMapsError,
  }));
  const isStaff = userIsStaff(currentUser);
  const loggedIn = currentUser && currentUser.email !== null;
  const hasGradProgram =
    currentUser &&
    currentUser.orgs &&
    currentUser.orgs.some((org) => org.hasGradProgram);
  const gMapsErrored = errored;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const subsite =
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'subsite' does not exist on type 'unknown... Remove this comment to see the full error message
    location.state && location.state.subsite
      ? // @ts-expect-error ts-migrate(2339) FIXME: Property 'subsite' does not exist on type 'unknown... Remove this comment to see the full error message
        location.state.subsite
      : content.subsite;
  const hasHomePage = !!content.subsite.homePage;
  const subsiteHasHomePage = hasHomePage || !!subsite.homePage;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line prefer-destructuring
  const heroSearch = content.heroSearch;
  const onlySearchResults = heroSearch && heroSearch.onlyResults;
  const [exposeSiteNav, setExposeSiteNav] = useState(false);
  const requestIsAmp = useContext(AmpContext);

  const onClickContent = (e) => {
    const el = e.target;
    const href = el.getAttribute('href');

    if (el.nodeName === 'A' && /^#/.test(href)) {
      e.preventDefault();
      scrollToHash(href);
    }
  };

  useEffect(() => {
    setDocumentTitle(content.headTitle || content.title);
  }, [content.headTitle, content.title]);
  useEffect(() => {
    if (hasParam(location.search, 'block')) {
      const el = document.getElementById(
        `cms-block-${getParam(location, 'block')}`
      );

      if (el) {
        window.scrollBy(0, el.getBoundingClientRect().top - 200);
      }
    }
  }, [location]);
  useEffect(() => {
    if (window.location.hash) {
      window.setTimeout(() => {
        scrollToHash(window.location.hash.split('?')[0], {
          padding: 32,
          noTransition: true,
        });
      }, 1);
    }
  }, []);
  useEffect(() => {
    const mapIsFullScreen = content.contentBlocks.some(
      (block) =>
        block.type === 'IDEALIST_DAYS_MAP' && block.data.defaultFullscreen
    );
    setExposeSiteNav(mapIsFullScreen);
  }, [content.contentBlocks]);
  // CMS pages might not have all the available translations
  const url =
    content.translations &&
    content.translations.reduce(
      (acc, cur) => {
        // eslint-disable-next-line no-param-reassign
        acc[cur.locale] = `/${cur.url}`;
        return acc;
      },
      {
        [content.locale]: `/${content.url}`,
      }
    );
  const staffTools = isStaff ? <StaffTools content={content} /> : null;
  const homePageUrl = subsite.homePage ? subsite.homePage.url : '';
  let lastBlock;
  let i = content.contentBlocks.length;

  const setLastBlock = () => {
    lastBlock =
      content.contentBlocks && content.contentBlocks.length
        ? content.contentBlocks[i]
        : null;
  };

  setLastBlock();

  while (lastBlock && lastBlock.type === 'SCROLL_TRACKING') {
    i -= 1;
    setLastBlock();
  }

  const fullWidthLayout =
    (content.layout === 'FULL_WIDTH' || content.layout === 'FULL_WIDTH_PLUS') &&
    !content.sidebar;
  const hideFooterDivider =
    isDefined(lastBlock) &&
    ((lastBlock.type === 'SPLIT_CONTENT' &&
      lastBlock.data.appearance === 'FULL_WIDTH' &&
      fullWidthLayout) ||
      [
        'GRAD_CTA_HERO',
        'CTA_HERO',
        'STICKY_HEADER_LINKS_GRAD',
        'STICKY_HEADER_LINKS',
        'IDEALIST_DAYS_MAP',
        'SCROLL_TRACKING',
      ].includes(lastBlock.type) ||
      hasBackground({
        block: lastBlock,
        fullWidthLayout,
      }));
  // Search bar at the top of page only should appear on "content pages"
  const hasPostSearchToolbar = subsiteHasHomePage && content.hasSearch;
  const ampHasPostSearchToolbar = subsiteHasHomePage && content.hasSearch;
  const contentPage = requestIsAmp ? (
    <AmpContentPage
      content={content}
      loggedIn={loggedIn}
      fullWidthLayout={fullWidthLayout}
      hasGradProgram={hasGradProgram}
      pageUrl={url[content.locale]}
      hasPostSearchToolbar={ampHasPostSearchToolbar}
      searchMode={false}
      onlySearchResults={onlySearchResults}
    />
  ) : (
    // TODO Fix jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions.
    //      The eslint-disable was added when updating packages and switching to TypeScript from Flow.
    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    <div onClick={onClickContent}>
      <ContentPage
        isStaff={isStaff}
        content={content}
        subsite={subsite}
        withBanner={isDefined(heroSearch)}
        loggedIn={loggedIn}
        currentUser={currentUser}
        hasGradProgram={hasGradProgram}
        pushFlashMessage={dispatch(pushFlashMessage)}
        updateCurrentUser={dispatch(updateCurrentUser)}
        pageUrl={url[content.locale]}
        locale={content.locale}
        homePageUrl={homePageUrl}
        history={history}
        showListingCounts={!!heroSearch && !!heroSearch.showCounts}
        fullWidthLayout={fullWidthLayout}
        gMapsErrored={gMapsErrored}
        setExposeSiteNav={setExposeSiteNav}
      />
    </div>
    /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  );
  const hero =
    heroSearch && heroSearch.onlyResults && !requestIsAmp
      ? undefined
      : heroSearch;
  const hasSearchHero = isDefined(hero);
  return (
    <Layout
      content={content}
      subsite={subsite}
      url={url}
      staffTools={staffTools}
      hero={hero}
      defaultWithSearchHero={hasSearchHero}
      searchHeroHasCta={
        hasSearchHero && Object.prototype.hasOwnProperty.call(hero, 'cta')
      }
      navSection="MAIN"
      searchMode={false}
      defaultSearchType={defaultSearchType}
      postSearchToolbar={
        hasPostSearchToolbar ? (
          <SearchToolbar
            content={content}
            subsite={subsite}
            searchMode={false}
          />
        ) : undefined
      }
      showListingCounts={heroSearch && heroSearch.showCounts}
      hideFooterDivider={hideFooterDivider}
      layout={content.layout}
      exposeSiteNav={exposeSiteNav}
    >
      {contentPage}
    </Layout>
  );
};

export default ContentPageContainer;
